<template>
  <!-- Proposal wrapper -->
  <div class="w-full overflow-x-hidden">
    <LoadingModal
      heading="Loading Proposal"
      :loading="loading" />
    <ProposalSettings
      :is-editable="isEditable"
      :show="showProposalSettings"
      @update:show="(v) => showProposalSettings = v" />
    <ProposalVersions
      v-if="isEditable"
      :proposal="store.proposal" />
    <ProposalLanguageSelector v-bind="$attrs" />
    <!-- Content sections -->
    <section v-if="store.proposal.accepted || !isEditable">
      <div class="flex justify-center gap-x-6 bg-indigo-600 px-6 py-2.5 sm:px-3.5">
        <p
          v-if="!isEditable"
          class="space-x-4 text-sm leading-6 text-white">
          <strong class="font-semibold">Read-only mode!</strong>
          <span>This proposal belongs to another user and you do not have the rights to edit it.</span>
        </p>
        <p
          v-if="store.proposal.accepted"
          class="text-sm leading-6 text-white">
          <strong class="font-semibold">This proposal (v{{ proposalVersions?.find(v => v.proposalVersionId === store.proposal.accepted_version?.id)?.proposalVersionNumber ?? 0 }}, id{{ proposalVersions?.find(v => v.proposalVersionId === store.proposal.accepted_version?.id)?.proposalVersionId ?? 0 }}) is accepted</strong>
        </p>
      </div>
    </section>
    <ProposalHero
      :content-editable="isEditable"
      @scroll-into-view="(v) => $refs[v.component].$el.scrollIntoView({ behavior: 'smooth' })"
      @show-proposal-settings="(v) => showProposalSettings = v" />
    <ProposalIntro
      ref="ProposalIntroComponent"
      :content-editable="isEditable" />
    <ProposalTransport
      ref="ProposalTransportComponent"
      :content-editable="isEditable" />
    <ProposalLodgings
      ref="ProposalLodgingsComponent"
      :content-editable="isEditable" />
    <ProposalProgram
      ref="ProposalProgramComponent"
      :content-editable="isEditable"
      :available-destinations="isEditable ? availableDestinations : []"
      v-bind="$attrs" />
    <ProposalActivities
      ref="ProposalActivitiesComponent"
      :content-editable="isEditable" />
    <ProposalEateries
      ref="ProposalEateriesComponent"
      :content-editable="isEditable" />
    <ProposalMediaGallery
      ref="ProposalMediaGalleryComponent"
      :content-editable="isEditable" />
    <!-- // TODO: Create ProposalInteractiveMap when dynamic data is available -->
    <!-- <ProposalInteractiveMap ref="ProposalInteractiveMapComponent" /> -->
    <ProposalPrices
      ref="ProposalPricesComponent"
      :content-editable="isEditable" />
  </div>
  <!-- /Proposal wrapper -->
</template>

<script setup>
/* external */
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted, ref, useAttrs, watch,
} from 'vue';

/* local */
import { useProposalStore } from '@/Stores/Proposal';
import ProposalEateries from '@/Pages/Proposal/Show/ProposalEateries.vue';
import ProposalIntro from '@/Pages/Proposal/Show/ProposalIntro.vue';
import ProposalActivities from '@/Pages/Proposal/Show/ProposalActivities.vue';
import ProposalLodgings from '@/Pages/Proposal/Show/ProposalLodgings.vue';
import ProposalMediaGallery from '@/Pages/Proposal/Show/ProposalMediaGallery.vue';
import ProposalTransport from '@/Pages/Proposal/Show/ProposalTransport.vue';
import ProposalProgram from '@/Pages/Proposal/Show/ProposalProgram.vue';
import ProposalPrices from '@/Pages/Proposal/Show/ProposalPrices.vue';
import ProposalHero from '@/Pages/Proposal/Show/ProposalHero.vue';
import LoadingModal from '@/Components/Modals/LoadingModal.vue';
import ProposalSettings from '@/Pages/Proposal/Components/ProposalSettings.vue';
import { notify } from 'notiwind';
import ProposalVersions from '@/Pages/Proposal/Components/ProposalVersions.vue';
import { usePage } from '@inertiajs/vue3';
import ProposalLanguageSelector from "@/Pages/Proposal/Components/ProposalLanguageSelector.vue";

/* *********************************************************************
 * Page declarations
 * ****************************************************************** */

const page = usePage();

const props = defineProps({
  databaseProposal: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  databaseProposalVersions: {
    type: Array,
    required: true,
    default: () => [],
  },
  availableDestinations: {
    type: Array,
    required: false,
    default: () => [],
  },
});

// Get inertia page attributes
const attrs = useAttrs();
// Use Pinia proposal store
const store = useProposalStore();
const proposalVersions = ref(props.databaseProposalVersions);

// Determine if the proposal is editable by current user
const isEditable = computed(() => attrs.permissions.includes('edit all proposals') || (attrs.permissions.includes('edit own proposals') && store.proposal.created_by === attrs.user.id));

// LoadingModal - should only be active on first load
const loading = ref(document.readyState === 'interactive' ?? false);
// Show proposal settings slide-in
const showProposalSettings = ref(false);

/* *********************************************************************
 * Page functions
 * ****************************************************************** */

/**
 * Function that will be called when the user tries to navigate away or refresh
 * @param {object} event - Event object
 * @returns {string|boolean} - Confirmation message
 */
function handleBeforeUnload(event) {
  const confirmationMessage = 'Are you sure you want to leave? You have unsaved changes!';
  if (store.unsavedChanges.state) {
    event.returnValue = confirmationMessage; // Legacy way for cross-browser support

    return confirmationMessage; // Standard way for modern browsers
  }

  return false;
}

/* *********************************************************************
 * Page lifecycle functions
 * ****************************************************************** */

onBeforeMount(() => {
  // Reset store
  store.$reset();
  // Set store with data from database
  store.setProposal({
    ...props.databaseProposal,
    terms: props.databaseProposal.brand?.terms,
  });
  store.isEditable = isEditable.value;
});

onMounted(() => {
  // Set brand if default exists
  if (store.proposal.brand.logoUrl === null) {
    if (page?.props?.team?.brands.find((brand) => brand.default)) {
      store.proposal.brand = page.props.team.brands.find((brand) => brand.default);
      store.proposal.brand_id = store.proposal.brand.id;
    }
  }
  // LoadingModal - if first load, we need to stop loading when completed
  document.onreadystatechange = () => {
    if (document.readyState === 'complete') {
      setTimeout(() => {
        loading.value = false;
      }, 500);
    }
  };
  // handleBeforeUnload - Setting up the beforeunload event listener when the component is created
  window.addEventListener('beforeunload', handleBeforeUnload);
});

/**
 * Watch for changes in the proposal object and save to database
 * @param {object} store.unsavedChanges - Unsaved changes object in Pinia store
 */
watch(store.unsavedChanges, () => {
  // Save changes to proposal fields to database
  if (store.unsavedChanges.changes.proposal || store.unsavedChanges.changes.proposalBrandSize) {
    store.setSaveTimer(
      () => {
        axios.post(route('api.proposal.store', { proposal: props.databaseProposal.id }), store.proposal)
          .then(() => {
            notify({
              type: 'success',
              title: store.unsavedChanges.changes.proposalBrandSize ? 'Updated' : 'Saved',
              text: store.unsavedChanges.changes.proposalBrandSize ? 'Brand size updated' : 'Proposal saved',
            }, 4000);
            // set unsaved changes of proposal to false after 100ms
            setTimeout(() => {
              store.unsavedChanges.changes.proposal = false;
              store.unsavedChanges.changes.proposalBrandSize = false;
            }, 100);
          }).catch((error) => {
            notify({
              type: 'danger',
              title: 'Something went wrong',
              text: 'Could not save proposal',
            }, 4000);
            console.log(error);
          }).finally(() => {
            //
          });
      },
      500,
    );
  }
});

onBeforeUnmount(() => {
  // Reset store
  store.$reset();
  // handleBeforeUnload - Cleaning up the beforeunload event listener when the component is about to unmount
  window.removeEventListener('beforeunload', handleBeforeUnload);
});

</script>
